import * as preact from "preact";

// Function to convert polar coordinates to cartesian coordinates
function polarToCartesian(
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number
): { x: number; y: number } {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
}

// Function to describe an arc using SVG path commands
function describeArc(
  x: number,
  y: number,
  radius: number,
  startAngle: number,
  endAngle: number
): string {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

  return [
    "M",
    start.x,
    start.y,
    "A",
    radius,
    radius,
    0,
    largeArcFlag,
    0,
    end.x,
    end.y,
  ].join(" ");
}

/**
 * Render a progress ring as a Preact SVG element using the current CSS color.
 *
 * @param innerRadius - Inner radius of the ring
 * @param outerRadius - Outer radius of the ring
 * @param arcAngle - Angle of the arc (in degrees) to represent progress
 */
export function ProgressRing({
  innerRadius,
  outerRadius,
  arcAngle,
}: {
  innerRadius: number;
  outerRadius: number;
  arcAngle: number;
}): preact.JSX.Element {
  const centerX = outerRadius;
  const centerY = outerRadius;
  const strokeWidth = outerRadius - innerRadius;
  const arcRadius = innerRadius + strokeWidth / 2;
  const svgSize = outerRadius * 2;
  const backgroundArc1 = describeArc(centerX, centerY, arcRadius, 0, 180);
  const backgroundArc2 = describeArc(centerX, centerY, arcRadius, 180, 360);
  const progressArc = describeArc(centerX, centerY, arcRadius, 0, arcAngle);

  return (
    <svg
      width={svgSize}
      height={svgSize}
      viewBox={`0 0 ${svgSize} ${svgSize}`}
    >
      <path
        d={backgroundArc1}
        fill="none"
        stroke="currentColor"
        stroke-opacity="0.35"
        stroke-width={strokeWidth}
      />
      <path
        d={backgroundArc2}
        fill="none"
        stroke="currentColor"
        stroke-opacity="0.35"
        stroke-width={strokeWidth}
      />
      <path
        d={progressArc}
        fill="none"
        stroke="currentColor"
        stroke-width={strokeWidth}
      />
    </svg>
  );
}
